/**
 * Who may use the app (docs/ACCOUNTS-PLAN.md section 5): every realm page needs an
 * account. Online that means a signed-in session. Offline - the server cannot be
 * reached - the account last signed in on this device keeps working until it signs
 * out, so a dropped connection never locks a writer out of their own realms.
 */

/** The account last signed in on this device: only what is needed to open its realms offline. */
export interface RememberedUser {
  readonly id: string;
  readonly email: string;
}

export type SessionStatus =
  | { readonly status: 'signed-in'; readonly user: RememberedUser }
  | { readonly status: 'signed-out' }
  | { readonly status: 'unavailable' };

export type Access =
  | { readonly kind: 'allowed'; readonly user: RememberedUser; readonly offline: boolean }
  | { readonly kind: 'sign-in' }
  | { readonly kind: 'unavailable' };

export function accessFor(session: SessionStatus, remembered: RememberedUser | null): Access {
  switch (session.status) {
    case 'signed-in':
      return { kind: 'allowed', user: session.user, offline: false };
    case 'signed-out':
      return { kind: 'sign-in' };
    case 'unavailable':
      return remembered ? { kind: 'allowed', user: remembered, offline: true } : { kind: 'unavailable' };
  }
}

const KEY = 'realmery.account';
const USER_ID = /^u_[A-Za-z0-9_-]{1,64}$/;

/** A user id is safe to use in storage names: the server's `u_` plus base64url. */
export function isUserId(id: string): boolean {
  return USER_ID.test(id);
}

export function rememberedUser(storage: Pick<Storage, 'getItem'> | undefined = globalThis.localStorage): RememberedUser | null {
  try {
    const value: unknown = JSON.parse(storage?.getItem(KEY) ?? 'null');
    if (value && typeof value === 'object' && 'id' in value && 'email' in value) {
      const { id, email } = value as Record<string, unknown>;
      if (typeof id === 'string' && isUserId(id) && typeof email === 'string') return { id, email };
    }
  } catch {
    // Unreadable or blocked storage: nobody is remembered.
  }
  return null;
}

export function rememberUser(user: RememberedUser | null, storage: Pick<Storage, 'setItem' | 'removeItem'> | undefined = globalThis.localStorage): void {
  try {
    if (user) storage?.setItem(KEY, JSON.stringify({ id: user.id, email: user.email }));
    else storage?.removeItem(KEY);
  } catch {
    // Blocked storage: the app still works online, just not offline.
  }
}

/** Where to go after unlocking (the `next` of /unlock): a path inside the app, never another site. */
export function safeNext(value: unknown): string | undefined {
  return typeof value === 'string' && value.startsWith('/') && !value.startsWith('//') && !value.startsWith('/\\') ? value : undefined;
}
